import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/outline';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import clsx from 'clsx';
import { NextRouter, useRouter } from 'next/router';
import { FC, useEffect, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';

import { programmaticScrollTo } from '@/utils/ScrollControl/scrollControl';

import { user } from '../../../../atoms/user';
import {
  EventDetails,
  logEvent,
} from '../../../../hooks/useClickEventTracking';
import { User } from '../../../../utils/interfaces/User';
import { ShouldRender } from '../../../shared/basic/ShouldRender';

interface FilterBarProps {
  data: { name: string; id: string }[];
  categoryLoading: boolean;
  pageName?: string;
  selectedCategory?: string;
}

const FilterBar: FC<FilterBarProps> = ({
  data,
  categoryLoading = false,
  pageName,
  selectedCategory,
}) => {
  const [currentIndex, setCurrentIndex] = useState<string>('');
  const sliderRef = useRef<HTMLDivElement | null>(null);
  const [isAtLeftmost, setIsAtLeftmost] = useState(true);
  const [isAtRightmost, setIsAtRightmost] = useState(false);

  const userData: User | undefined = useRecoilValue(user);
  const screens = useBreakpoint();
  const router = useRouter();

  const scrollLeft = () => {
    sliderRef.current?.scrollBy({
      left: -300,
      behavior: 'smooth',
    });
  };

  const scrollRight = () => {
    sliderRef.current?.scrollBy({
      left: 300,
      behavior: 'smooth',
    });
  };

  const scrollToElement = (element: HTMLElement) => {
    const parent = sliderRef.current;
    if (parent && element) {
      const parentRect = parent.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();

      const offsetLeft = elementRect.left - parentRect.left;
      const offsetRight = elementRect.right - parentRect.right;

      if (offsetLeft < 100) {
        parent.scrollBy({
          left: offsetLeft - 200,
          behavior: 'smooth',
        });
      } else if (offsetRight > -100) {
        parent.scrollBy({
          left: offsetRight + 200,
          behavior: 'smooth',
        });
      }
    }
  };
  useEffect(() => {
    const handleScroll = () => {
      if (sliderRef.current) {
        const isLeftmost = sliderRef.current.scrollLeft === 0;
        const isRightmost =
          sliderRef.current?.scrollLeft + sliderRef.current?.clientWidth + 50 >=
          sliderRef.current?.scrollWidth;

        setIsAtLeftmost(isLeftmost);
        setIsAtRightmost(isRightmost);
      }
    };
    handleScroll();

    sliderRef.current?.addEventListener('scroll', handleScroll);

    return () => {
      sliderRef.current?.removeEventListener('scroll', handleScroll);
    };
  }, [data, categoryLoading]);
  useEffect(() => {
    if (selectedCategory !== undefined) {
      setCurrentIndex(selectedCategory);
    }
  }, [selectedCategory]);
  // const isExceedingWindowWidth =
  //   windowWidth !== undefined &&
  //   sliderRef.current?.scrollWidth !== undefined &&
  //   windowWidth < sliderRef.current?.scrollWidth;

  const safePushWithScrollRestore = (
    router: NextRouter,
    url: Parameters<NextRouter['push']>[0],
    as?: Parameters<NextRouter['push']>[1],
    options: Parameters<NextRouter['push']>[2] = {},
  ) => {
    if (typeof window === 'undefined' || !router?.push) return;

    const scrollY = window.scrollY;

    router
      .push(url, as, { scroll: false, shallow: true, ...options })
      .then(() => {
        programmaticScrollTo(
          {
            top: scrollY,
            behavior: 'auto',
          },
          0,
        );
      })
      .catch((err) => {
        console.error('Navigation failed:', err);
      });
  };

  return (
    <>
      <ShouldRender check={screens.md}>
        <div className='flex items-center  relative'>
          <div className='relative  overflow-hidden w-full'>
            <div
              ref={sliderRef}
              className='flex  overflow-hidden w-full relative'
              style={{ scrollBehavior: 'smooth', scrollbarWidth: 'none' }}
            >
              {!categoryLoading && data?.length > 1 ? (
                data.map((item, index) => (
                  <div
                    className={clsx(
                      item?.id === currentIndex || selectedCategory === item.id
                        ? 'border-b border-white text-white'
                        : 'border-b border-cartBgColor',
                      'whitespace-nowrap py-2.5 px-4  text-sm cursor-pointer',
                    )}
                    key={index}
                    onClick={() => {
                      setCurrentIndex(item?.id);
                      safePushWithScrollRestore(router, {
                        pathname: router.pathname,
                        query: { filter: item.name },
                      });
                      const eventParams: EventDetails = {
                        event_name: 'view_category',
                        source: pageName as string,
                        source_type: 'category',
                        source_id: item.id,
                        sub_source: null,
                        sub_source_id: null,
                      };

                      logEvent({
                        eventDetails: eventParams,
                        userData: userData,
                        pathname: router.pathname,
                      });
                    }}
                  >
                    <span>{item?.name}</span>
                  </div>
                ))
              ) : (
                <div className='bg-cartBgColor w-full rounded-lg h-10 animate-pulse'></div>
              )}
            </div>
            {!isAtRightmost && (
              <div className='absolute h-full transform top-0 right-0 '>
                <div className='flex items-center h-full justify-center'>
                  <div
                    onClick={scrollRight}
                    className='z-10 w-8 h-8 rounded-full flex cursor-pointer justify-center items-center text-white hover:border hover:border-gray-200'
                  >
                    <ChevronRightIcon className='w-4 h-4 text-white' />
                  </div>
                </div>
                <div className='absolute z-0 bg-gradient-to-l from-black via-transparent to-transparent h-full w-10 xl:w-36 right-0 top-0 rounded-xl'></div>
              </div>
            )}
          </div>

          {!isAtLeftmost && (
            <div className={clsx('absolute h-full top-0 left-0')}>
              <div className='flex items-center h-full justify-center'>
                <div
                  onClick={scrollLeft}
                  className='z-10 w-8 h-8 rounded-full flex cursor-pointer justify-center items-center text-white hover:border border-gray-200'
                >
                  <ChevronLeftIcon className='w-4 h-4 text-white' />
                </div>
              </div>
              <div className='absolute z-0 bg-gradient-to-r from-black via-transparent to-transparent h-full w-10 xl:w-36 left-0 top-0'></div>
            </div>
          )}
        </div>
      </ShouldRender>
      <ShouldRender check={!screens.md}>
        <div className='relative'>
          <div
            ref={sliderRef}
            className='flex  overflow-auto   hide-scrollbar'
            style={{ scrollBehavior: 'smooth', scrollbarWidth: 'none' }}
          >
            {data.length > 1 ? (
              data.map((item, index) => (
                <div
                  className={clsx(
                    item?.id === currentIndex || selectedCategory === item.id
                      ? 'border-b border-white text-white'
                      : 'border-b border-cartBgColor',
                    'whitespace-nowrap py-2.5 px-4  text-sm cursor-pointer',
                  )}
                  onClick={(e) => {
                    setCurrentIndex(item?.id);
                    scrollToElement(e.currentTarget);
                    safePushWithScrollRestore(router, {
                      pathname: router.pathname,
                      query: { filter: item.name },
                    });
                    const eventParams: EventDetails = {
                      event_name: 'view_category',
                      source: pageName as string,
                      source_type: 'category',
                      source_id: item.id,
                      sub_source: null,
                      sub_source_id: null,
                    };

                    logEvent({
                      eventDetails: eventParams,
                      userData: userData,
                      pathname: router.pathname,
                    });
                  }}
                  key={index}
                >
                  <span>{item?.name}</span>
                </div>
              ))
            ) : (
              <div className='bg-cartBgColor w-full rounded-lg h-10 animate-pulse'></div>
            )}
          </div>

          {!isAtRightmost && (
            <div className='absolute h-full transform top-0 right-0'>
              <div className='flex items-center h-full justify-center'>
                <div
                  onClick={scrollRight}
                  className='z-10 w-8 h-8 rounded-full flex cursor-pointer justify-center items-center text-white '
                >
                  <ChevronRightIcon className='w-4 h-4 text-white' />
                </div>
              </div>
              <div className='absolute z-0 bg-gradient-to-l from-black via-transparent to-transparent h-full w-20 xl:w-36 right-0 top-0 rounded-xl'></div>
            </div>
          )}

          {!isAtLeftmost && (
            <div className={clsx('absolute h-full top-0 left-0')}>
              <div className='flex items-center h-full justify-center'>
                <div
                  onClick={scrollLeft}
                  className='z-10 w-8 h-8 rounded-full flex cursor-pointer  justify-center items-center text-white  '
                >
                  <ChevronLeftIcon className='w-4 h-4 text-white' />
                </div>
              </div>
              <div className='absolute z-0 bg-gradient-to-r from-black via-transparent to-transparent h-full w-10 xl:w-36 left-0 top-0'></div>
            </div>
          )}
        </div>
      </ShouldRender>
    </>
  );
};

export default FilterBar;
